import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Clock, Scale, Truck, User, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { getCurrentUser } from '@/lib/auth';

interface TransportationRecord {
  id: number;
  visitId: number;
  plateNumber: string;
  driverName: string;
  transporterName: string;
  orderExternalId?: string;
  product?: string;
  clientOrderNumber?: string;
  actualCheckedInTime?: string;
  actualExitTime?: string;
  timeInLot?: number; // in minutes
  weighbridgeEntryWeight?: number;
  weighbridgeExitWeight?: number;
  netWeight?: number;
  isNonMatched: boolean;
  accessDecision: string;
  migratedAt: string;
  originalCreatedAt: string;
}

const TransportationPage: React.FC = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const currentUser = getCurrentUser();

  if (currentUser?.role === 'port') {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-12">
            <Truck className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">Registry Has Not Been Implemented</h3>
            <p className="text-gray-600">Will be available soon!</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { data: transportationData = [], isLoading, error, refetch } = useQuery<TransportationRecord[]>({
    queryKey: ['/api/transportation'],
    queryFn: async () => {
      const response = await fetch('/api/transportation', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-Tenant-Id': localStorage.getItem('tenant_id') || '',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch transportation data');
      }
      
      return response.json();
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return 'N/A';
    return format(new Date(timeString), 'HH:mm:ss');
  };

  const formatDate = (timeString?: string) => {
    if (!timeString) return 'N/A';
    return format(new Date(timeString), 'MMM dd, yyyy');
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusBadge = (record: TransportationRecord) => {
    if (record.isNonMatched) {
      return <Badge variant="outline" className="bg-orange-200 text-orange-800 border-orange-400">Non-Matched ANPR</Badge>;
    }
    
    switch (record.accessDecision) {
      case 'PASS':
        return <Badge variant="outline" className="bg-green-200 text-green-800 border-green-400">Passed</Badge>;
      case 'HOLD':
        return <Badge variant="outline" className="bg-red-200 text-red-800 border-red-400">Held</Badge>;
      case 'MANUAL_REVIEW':
        return <Badge variant="outline" className="bg-yellow-200 text-yellow-800 border-yellow-400">Manual Review</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading transportation history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-red-600">
          <p>Error loading transportation data</p>
          <Button onClick={handleRefresh} className="mt-4">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Transportation Registry</h1>
          <p className="text-gray-600 mt-2">Historical records of departed trucks</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {transportationData.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Truck className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No Transportation Records</h3>
            <p className="text-gray-600">No departed trucks have been moved to history yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {transportationData.map((record) => (
            <Card key={record.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Truck className="h-5 w-5" />
                      {record.plateNumber}
                    </CardTitle>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {record.driverName}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(record.migratedAt)}
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(record)}
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Timing Information */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Timing
                    </h4>
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-gray-600">Checked In:</span>
                        <br />
                        <span className="font-mono">{formatTime(record.actualCheckedInTime)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Exited:</span>
                        <br />
                        <span className="font-mono">{formatTime(record.actualExitTime)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Time in Lot:</span>
                        <br />
                        <span className="font-semibold text-blue-600">{formatDuration(record.timeInLot)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Weight Information */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-1">
                      <Scale className="h-4 w-4" />
                      Weights
                    </h4>
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-gray-600">Entry:</span>
                        <br />
                        <span className="font-mono">{record.weighbridgeEntryWeight ? `${record.weighbridgeEntryWeight} kg` : 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Exit:</span>
                        <br />
                        <span className="font-mono">{record.weighbridgeExitWeight ? `${record.weighbridgeExitWeight} kg` : 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Net:</span>
                        <br />
                        <span className="font-semibold text-green-600">{record.netWeight ? `${record.netWeight} kg` : 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Order Information */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-gray-700">Order Details</h4>
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-gray-600">Transporter:</span>
                        <br />
                        <span>{record.transporterName || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Order ID:</span>
                        <br />
                        <span className="font-mono text-xs">{record.orderExternalId || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Client Order:</span>
                        <br />
                        <span className="font-mono text-xs">{record.clientOrderNumber || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-gray-700">Additional Info</h4>
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-gray-600">Visit ID:</span>
                        <br />
                        <span className="font-mono text-xs">#{record.visitId}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Migrated:</span>
                        <br />
                        <span className="text-xs">{formatDate(record.migratedAt)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Original Created:</span>
                        <br />
                        <span className="text-xs">{formatDate(record.originalCreatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {transportationData.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-600">
          Showing {transportationData.length} transportation record{transportationData.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default TransportationPage;